import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import panelApi from "@/lib/panelApi";
import api from "@/lib/api";
import AcenteCommandPalette from "./AcenteCommandPalette";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertTriangle, Check as CheckIcon } from "lucide-react";
import {
  LogOut,
  LayoutDashboard,
  Calendar,
  CalendarCheck,
  Users,
  Building2,
  UtensilsCrossed,
  BedDouble,
  BarChart3,
  ScrollText,
  Trash2,
  Wallet,
  Archive,
  ShieldCheck,
  TrendingUp,
  FileEdit,
  Upload,
  Search,
  Moon,
  Sun,
  Bell,
  Plus,
  Receipt,
  TableProperties,
  MessageCircle,
  Settings,
  KeyRound,
  Sparkles,
  ClipboardList,
  Wrench,
  ChevronDown,
} from "lucide-react";

const ROLE_LABELS = {
  admin: "Yönetici",
  acente: "Acente",
  operasyon: "Operasyon",
  ozel_acente: "Özel Acente",
  saha_calisani: "Saha Çalışanı",
};

// Sidebar items can carry an optional `section` string. Items are grouped
// under a small uppercase header inside the nav. Items without a section
// render inline (no header) to keep the top of the sidebar clean.
const ALL_NAV = [
  { to: "/acente", key: "dashboard", label: "Panel", icon: LayoutDashboard, roles: ["admin", "acente", "operasyon", "ozel_acente"] },
  { to: "/acente/rezervasyonlar", key: "reservations", label: "Rezervasyonlar", icon: Calendar, roles: ["admin", "acente", "ozel_acente"] },
  { to: "/acente/bugun-girisler", key: "today", label: "Bugün Girişler", icon: CalendarCheck, roles: ["admin", "operasyon"] },
  { to: "/acente/takip", key: "tracking", label: "Rezervasyon Takip", icon: TableProperties, roles: ["admin", "operasyon"] },
  { to: "/acente/rezervasyon/yeni", key: "new", label: "Yeni Rezervasyon", icon: Plus, roles: ["acente", "ozel_acente"] },
  { to: "/acente/toplu-yukle", key: "bulk", label: "Toplu Yükle", icon: Upload, roles: ["admin", "acente"] },
  { to: "/acente/planlama-raporlar", key: "planning", label: "Planlama & Rapor", icon: BarChart3, roles: ["admin", "operasyon", "acente"] },
  { to: "/acente/toplanan-ucretler", key: "collected_payments", label: "Toplanan Ücretler", icon: Wallet, roles: ["admin", "operasyon"] },
  { to: "/acente/aylik-rapor", key: "monthly_report", label: "Aylık Rapor", icon: BarChart3, roles: ["ozel_acente", "acente"] },
  // Kolay Araçlar — batch tools for agencies + admin. Operasyon focuses on
  // pricing review and doesn't create programs / room lists.
  { to: "/acente/grup-programi", key: "group_program", label: "Grup Programı", icon: ClipboardList, roles: ["admin", "acente"], section: "Kolay Araçlar", toolGroup: "admin" },
  { to: "/acente/oda-listesi", key: "room_list", label: "Oda Listesi", icon: BedDouble, roles: ["admin", "acente"], section: "Kolay Araçlar", toolGroup: "admin" },
  { to: "/acente/revizyon-talepleri", key: "revisions", label: "Revizyon Talepleri", icon: FileEdit, roles: ["admin", "operasyon", "acente"], badgeKey: "revisions" },
  { to: "/acente/fatura-talepleri", key: "invoices", label: "Fatura Talepleri", icon: Receipt, roles: ["admin", "acente", "ozel_acente"], badgeKey: "invoices" },
  { to: "/acente/fiyat-onaylari", key: "ops_pricing", label: "Fiyat Onayları", icon: Wallet, roles: ["operasyon"], badgeKey: "ops_pricing" },
  { to: "/acente/sifre-talepleri", key: "pwresets", label: "Şifre Talepleri", icon: KeyRound, roles: ["admin", "operasyon"], badgeKey: "pwresets" },
  { to: "/acente/acenteler", key: "agencies", label: "Acenteler", icon: Building2, roles: ["admin"], toolGroup: "admin" },
  { to: "/acente/oteller", key: "hotels", label: "Otel Listesi", icon: BedDouble, roles: ["admin", "operasyon"], toolGroup: "admin" },
  { to: "/acente/kullanicilar", key: "users", label: "Kullanıcılar", icon: Users, roles: ["admin"], toolGroup: "admin" },
  { to: "/acente/loglar", key: "logs", label: "Sistem Logları", icon: ScrollText, roles: ["admin"], toolGroup: "admin" },
  { to: "/acente/silinen-kayitlar", key: "trash", label: "Silinen Kayıtlar", icon: Trash2, roles: ["admin"], toolGroup: "admin" },
  { to: "/acente/silinen-sirketler", key: "deleted_agencies", label: "Silinen Şirketler", icon: Archive, roles: ["admin"], toolGroup: "admin" },
  { to: "/acente/ayarlar", key: "settings", label: "Ayarlar", icon: Settings, roles: ["admin", "acente", "operasyon", "ozel_acente"] },
];

// Role-tinted role badge palette.
const ROLE_TINT = {
  admin: "bg-brand-navy text-brand-gold",
  acente: "bg-brand-orange/15 text-brand-orange-dark",
  operasyon: "bg-blue-100 text-blue-700",
  ozel_acente: "bg-emerald-100 text-emerald-700",
};

const THEME_KEY = "acente_theme"; // "light" | "dark"

export default function AcenteLayout() {
  const { user, logout } = useAcenteAuth();
  const nav = useNavigate();
  const role = user?.role;
  const items = ALL_NAV.filter((n) => n.roles.includes(role));

  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) === "dark"; } catch { return false; }
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [badges, setBadges] = useState({ revisions: 0, invoices: 0, pwresets: 0, ops_pricing: 0 });
  const [supportWhatsApp, setSupportWhatsApp] = useState("");

  // Load public settings to pick up the support WhatsApp number (admin-editable).
  useEffect(() => {
    api.get("/settings").then((r) => {
      const map = Object.fromEntries((r.data || []).map((s) => [s.key, s.value]));
      setSupportWhatsApp(String(map.acente_support_whatsapp || "").trim());
    }).catch(() => {});
  }, []);

  // Apply theme class on <html> so tailwind's dark: variants react everywhere
  // BUT scope it to /acente only — remove on unmount so the public site is
  // unaffected.
  useEffect(() => {
    const el = document.documentElement;
    if (dark) el.classList.add("acente-dark");
    else el.classList.remove("acente-dark");
    try { localStorage.setItem(THEME_KEY, dark ? "dark" : "light"); } catch {}
    return () => { el.classList.remove("acente-dark"); };
  }, [dark]);

  // Ctrl+K / Cmd+K palette shortcut. Also 'd' toggles dark mode when palette
  // is closed. 'n' navigates to new reservation.
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Poll pending revision and invoice requests → sidebar badges (lightweight)
  // pwresets endpoint requires admin/operasyon; other roles' calls would 403 → catch silently.
  // Özel acente doesn't see revisions at all — skip the fetch to avoid noise.
  const refreshBadges = useCallback(() => {
    const role = user?.role;
    const isStaff = role === "admin" || role === "operasyon";
    Promise.all([
      role === "ozel_acente"
        ? Promise.resolve({ data: [] })
        : panelApi.get("/revisions", { params: { status: "beklemede" } }).catch(() => ({ data: [] })),
      panelApi.get("/invoice-requests", { params: { status: "beklemede" } }).catch(() => ({ data: [] })),
      role === "acente" || role === "ozel_acente"
        ? Promise.resolve({ data: [] })
        : panelApi.get("/password-reset-requests", { params: { status: "beklemede" } }).catch(() => ({ data: [] })),
      // Ops pricing queue count — only fetched for admin/operasyon.
      isStaff
        ? panelApi.get("/reservations", { params: { ops_pending: true } }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
      // Field issues raised by saha_calisani. Only admin/operasyon care.
      isStaff
        ? panelApi.get("/field-issues").catch(() => ({ data: { total_unread: 0, items: [] } }))
        : Promise.resolve({ data: { total_unread: 0, items: [] } }),
    ]).then(([rev, inv, pw, ops, fld]) => {
      const fieldUnread = (fld.data && typeof fld.data.total_unread === "number") ? fld.data.total_unread : 0;
      const fieldItems  = (fld.data && Array.isArray(fld.data.items)) ? fld.data.items : [];
      setBadges((prev) => {
        // Live toast pop when a new saha issue lands (skip on first mount so
        // the user isn't spammed with an alert for stale unread items).
        if (isStaff && prev.__initted && fieldUnread > (prev.field_issues || 0)) {
          const delta = fieldUnread - (prev.field_issues || 0);
          try {
            const first = fieldItems.find((x) => x.unread);
            toast.warning(
              delta === 1
                ? `Yeni saha bildirimi: ${first?.group_name || first?.reservation_number || "Bir rezervasyon"} · ${first?.hotel_name || ""}`
                : `${delta} yeni saha bildirimi geldi`,
              {
                duration: 7000,
                action: {
                  label: "Aç",
                  onClick: () => nav(first ? `/acente/rezervasyon/${first.id}` : "/acente/fiyat-onaylari"),
                },
              }
            );
          } catch { /* toast import missing? no-op */ }
        }
        return {
          revisions:   Array.isArray(rev.data) ? rev.data.length : 0,
          invoices:    Array.isArray(inv.data) ? inv.data.length : 0,
          pwresets:    Array.isArray(pw.data) ? pw.data.length : 0,
          ops_pricing: Array.isArray(ops.data) ? ops.data.length : 0,
          field_issues: fieldUnread,
          field_issue_items: fieldItems.slice(0, 5),
          __initted: true,
        };
      });
    });
  }, [user?.role, nav]);
  useEffect(() => {
    refreshBadges();
    const id = setInterval(refreshBadges, 60_000); // 60s poll
    return () => clearInterval(id);
  }, [refreshBadges]);

  const handleLogout = () => {
    logout();
    nav("/acente/giris");
  };

  const totalBadge = badges.revisions + badges.invoices + badges.pwresets + badges.ops_pricing + (badges.field_issues || 0);

  // Separate the sidebar items into two buckets for the admin role:
  //  1. Regular top-level items (rendered inline as before)
  //  2. Tools grouped under a collapsible "Araçlar" dropdown to declutter
  //     the sidebar for admins (which otherwise shows 15+ links).
  // For non-admin roles, every item renders inline — the toolGroup flag is
  // simply ignored.
  const isAdmin = role === "admin";
  const mainItems = items.filter((it) => !(isAdmin && it.toolGroup === "admin"));
  const toolItems = isAdmin ? items.filter((it) => it.toolGroup === "admin") : [];

  const [toolsOpen, setToolsOpen] = useState(() => {
    try { return localStorage.getItem("acente_tools_open") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("acente_tools_open", toolsOpen ? "1" : "0"); } catch {}
  }, [toolsOpen]);
  // Sum badges inside the tools group (currently just `trash` has one via
  // ops_pricing/pwresets/etc — most tools have zero). This shows a count on
  // the dropdown header when collapsed so the admin still sees pending
  // items without expanding.
  const toolsBadge = toolItems.reduce((s, it) => s + (it.badgeKey ? (badges[it.badgeKey] || 0) : 0), 0);

  // Format WhatsApp number for wa.me and personalise the auto-message with
  // the current agency name (and user name / role) so the support desk knows
  // exactly who is writing without having to ask.
  const supportHref = (() => {
    if (!supportWhatsApp) return null;
    const digits = supportWhatsApp.replace(/[^\d]/g, "");
    if (!digits) return null;
    const agency = (user?.agency_name || "").trim();
    const senderName = (user?.name || "").trim();
    const roleLabel = ROLE_LABELS[user?.role] || "";
    // Compose a Turkish greeting that leads with the agency name so the
    // support agent sees "who" before "what".
    const lines = ["Merhaba, Ariaf ASIS Acente Paneli üzerinden destek talep ediyorum."];
    if (agency) lines.push(`Acente: ${agency}`);
    if (senderName || roleLabel) {
      lines.push(
        `Ben: ${[senderName, roleLabel ? `(${roleLabel})` : ""].filter(Boolean).join(" ").trim()}`
      );
    }
    const msg = encodeURIComponent(lines.join("\n"));
    return `https://wa.me/${digits}?text=${msg}`;
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-canvas-cream via-canvas-cream to-brand-gold/10 acente-theme-scope acente-print-scope">
      {/* Warm branded header */}
      <header className="relative overflow-hidden bg-gradient-to-r from-brand-navy via-brand-navy to-brand-navy/95 text-white shadow-lg no-print" data-testid="acente-header">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-brand-gold blur-3xl" />
          <div className="absolute -bottom-16 -left-10 w-72 h-72 rounded-full bg-brand-orange blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-2 sm:gap-4">
          <div className="min-w-0 flex items-center gap-3">
            <img
              src="/asis-logo.png"
              alt="ASIS"
              className="w-10 h-10 sm:w-11 sm:h-11 object-contain drop-shadow-md flex-shrink-0 hidden sm:block"
              data-testid="acente-header-asis-logo"
            />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.4em] text-brand-gold font-semibold hidden sm:block">Ariaf ASIS Acente Paneli</div>
              <div className="font-serif text-lg sm:text-2xl mt-0.5 truncate" data-testid="acente-header-title">Acente Rezervasyon Sistemi</div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Search (Ctrl+K) trigger */}
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              data-testid="acente-search-trigger"
              className="hidden sm:inline-flex items-center gap-2 rounded-full pl-3 pr-2 py-1.5 text-xs bg-white/10 hover:bg-white/15 backdrop-blur transition"
              title="Hızlı ara (Ctrl+K)"
            >
              <Search size={13} />
              <span className="text-white/85">Ara…</span>
              <span className="ml-2 rounded bg-white/15 px-1.5 py-0.5 text-[10px] font-mono">⌘K</span>
            </button>

            {/* Support / Danışma Hattı (admin-configurable WhatsApp) */}
            {supportHref && (
              <a
                href={supportHref}
                target="_blank"
                rel="noreferrer"
                data-testid="acente-support-btn"
                title="Danışma Hattı — WhatsApp"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs bg-emerald-500/90 hover:bg-emerald-500 text-white transition"
              >
                <MessageCircle size={13} />
                <span className="hidden md:inline">Danışma Hattı</span>
              </a>
            )}

            {/* Notification badge — hidden for özel acente since they don't
                have revisions. */}
            {user?.role !== "ozel_acente" && (
              <button
                type="button"
                onClick={() => nav("/acente/revizyon-talepleri")}
                data-testid="acente-notif-btn"
                className="relative inline-flex items-center justify-center rounded-full w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur transition"
                title="Bekleyen revizyon talepleri"
              >
                <Bell size={15} />
                {totalBadge > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-orange text-white text-[10px] font-bold flex items-center justify-center shadow-md"
                    data-testid="acente-notif-badge"
                  >
                    {totalBadge > 99 ? "99+" : totalBadge}
                  </span>
                )}
              </button>
            )}

            {/* Saha bildirim rozeti — admin/operasyon-only, ayrı bir düğme.
                Popover ile son 5 saha bildirimini gösterir. */}
            {(user?.role === "admin" || user?.role === "operasyon") && (
              <SahaIssueBell
                items={badges.field_issue_items || []}
                unread={badges.field_issues || 0}
                onOpenOne={(rid) => nav(`/acente/rezervasyon/${rid}`)}
                onAcknowledge={async (rid) => {
                  try {
                    await panelApi.post(`/field-issues/${rid}/acknowledge`);
                    refreshBadges();
                  } catch { /* silent */ }
                }}
              />
            )}

            {/* Theme toggle */}
            <button
              type="button"
              onClick={() => setDark((v) => !v)}
              data-testid="acente-theme-toggle"
              className="inline-flex items-center justify-center rounded-full w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur transition"
              title={dark ? "Aydınlık moda geç" : "Karanlık moda geç"}
            >
              {dark ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            <div className="text-right hidden md:block ml-2">
              <div className="text-sm font-medium" data-testid="acente-header-user">{user?.name}</div>
              <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 mt-0.5 bg-white/10">
                <ShieldCheck size={11} /> {ROLE_LABELS[role] || role}
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="acente-logout-btn"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm bg-white/10 hover:bg-white/20 backdrop-blur transition ml-1"
            >
              <LogOut size={14}/> <span className="hidden sm:inline">Çıkış</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid gap-6 lg:grid-cols-[240px_1fr]">
        {/* Colorful sidebar */}
        <aside
          className="bg-white rounded-2xl border border-brand-navy/10 shadow-md p-3 h-fit sticky top-6 overflow-hidden acente-side no-print"
          data-testid="acente-sidebar"
        >
          <div className={`mb-3 rounded-xl p-3 text-center ${ROLE_TINT[role] || "bg-slate-100"}`}>
            <div className="text-[10px] uppercase tracking-widest opacity-80">Rol</div>
            <div className="text-sm font-semibold mt-0.5">{ROLE_LABELS[role] || role}</div>
            {user?.agency_name && (
              <div className="text-[10px] mt-1 opacity-80 truncate">{user.agency_name}</div>
            )}
          </div>
          <nav className="flex flex-row lg:flex-col gap-1 overflow-x-auto">
            {(() => {
              // Group nav items by their `section` header so we can insert a
              // small uppercase label between groups. Items with no section
              // render inline (no header) — keeps the sidebar top clean.
              const nodes = [];
              let lastSection = null;
              mainItems.forEach((it) => {
                if (it.section && it.section !== lastSection) {
                  nodes.push(
                    <div
                      key={`section-${it.section}`}
                      className="hidden lg:block px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest text-brand-orange/80 font-semibold"
                      data-testid={`acente-nav-section-${it.section}`}
                    >
                      {it.section}
                    </div>
                  );
                  lastSection = it.section;
                } else if (!it.section) {
                  lastSection = null;
                }
                const Icon = it.icon;
                const badge = it.badgeKey ? badges[it.badgeKey] : 0;
                nodes.push(
                  <NavLink
                    key={it.key}
                    to={it.to}
                    end={it.to === "/acente"}
                    data-testid={`acente-nav-${it.key}`}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-md shadow-brand-orange/25"
                          : "text-brand-navy/80 hover:bg-brand-orange/5 hover:text-brand-orange-dark"
                      }`
                    }
                  >
                    <Icon size={16} />
                    <span className="flex-1">{it.label}</span>
                    {badge > 0 && (
                      <span
                        className="ml-auto inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full bg-brand-orange text-white text-[10px] font-semibold"
                        data-testid={`acente-nav-badge-${it.key}`}
                      >
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                  </NavLink>
                );
              });
              return nodes;
            })()}

            {/* Admin-only "Araçlar" collapsible group. Groups 6 admin-heavy
                links (Acenteler, Otel Listesi, Kullanıcılar, Sistem Logları,
                Silinen Kayıtlar, Silinen Şirketler) + shared tools under one
                header. Toggle state persists via localStorage so opening it
                once keeps it open across page navigations. */}
            {isAdmin && toolItems.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-100 hidden lg:block" data-testid="acente-nav-tools-group">
                <button
                  type="button"
                  onClick={() => setToolsOpen((v) => !v)}
                  aria-expanded={toolsOpen}
                  data-testid="acente-nav-tools-toggle"
                  className={`w-full group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    toolsOpen
                      ? "bg-brand-navy/5 text-brand-navy"
                      : "text-brand-navy/80 hover:bg-brand-orange/5 hover:text-brand-orange-dark"
                  }`}
                >
                  <Wrench size={16} />
                  <span className="flex-1 text-left font-medium">Araçlar</span>
                  {toolsBadge > 0 && !toolsOpen && (
                    <span
                      className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full bg-brand-orange text-white text-[10px] font-semibold"
                      data-testid="acente-nav-tools-badge"
                    >
                      {toolsBadge > 99 ? "99+" : toolsBadge}
                    </span>
                  )}
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${toolsOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {toolsOpen && (
                  <div
                    className="mt-1 ml-2 pl-2 border-l-2 border-brand-orange/30 flex flex-col gap-0.5"
                    data-testid="acente-nav-tools-panel"
                  >
                    {toolItems.map((it) => {
                      const Icon = it.icon;
                      const badge = it.badgeKey ? badges[it.badgeKey] : 0;
                      return (
                        <NavLink
                          key={it.key}
                          to={it.to}
                          data-testid={`acente-nav-${it.key}`}
                          className={({ isActive }) =>
                            `group flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] whitespace-nowrap transition-all ${
                              isActive
                                ? "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-sm shadow-brand-orange/25"
                                : "text-brand-navy/75 hover:bg-brand-orange/5 hover:text-brand-orange-dark"
                            }`
                          }
                        >
                          <Icon size={14} />
                          <span className="flex-1">{it.label}</span>
                          {badge > 0 && (
                            <span
                              className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-orange text-white text-[10px] font-semibold"
                              data-testid={`acente-nav-badge-${it.key}`}
                            >
                              {badge > 99 ? "99+" : badge}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Mobile: on <lg screens the sidebar collapses into a horizontal
                scroller. Rendering the admin tools inline here keeps them
                accessible on mobile without a nested dropdown UI. */}
            {isAdmin && toolItems.length > 0 && (
              <div className="lg:hidden flex flex-row gap-1" data-testid="acente-nav-tools-mobile">
                {toolItems.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={`m-${it.key}`}
                      to={it.to}
                      className={({ isActive }) =>
                        `group flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-all ${
                          isActive
                            ? "bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white shadow-md shadow-brand-orange/25"
                            : "text-brand-navy/80 hover:bg-brand-orange/5 hover:text-brand-orange-dark"
                        }`
                      }
                    >
                      <Icon size={16} />
                      <span>{it.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </nav>

          <div className="mt-4 pt-3 border-t border-slate-100 text-[10px] text-slate-400 text-center">
            İpucu: <span className="font-mono">Ctrl+K</span> ile hızlı ara
          </div>
        </aside>

        <main className="min-w-0 acente-print-area" data-testid="acente-main">
          <Outlet />
        </main>
      </div>

      <AcenteCommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        role={role}
        isDark={dark}
        onToggleTheme={() => setDark((v) => !v)}
      />
    </div>
  );
}

function _fmtStamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

/**
 * SahaIssueBell — admin/operasyon-only header bell that shows the 5 most
 * recent field-staff issue reports in a popover. Live-updated by
 * `refreshBadges` every 60s; a toast pops when new items arrive. Clicking
 * an item jumps to the reservation detail page and marks the issue as
 * acknowledged so it drops off the unread counter.
 */
function SahaIssueBell({ items = [], unread = 0, onOpenOne, onAcknowledge }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="saha-issue-bell"
          className="relative inline-flex items-center justify-center rounded-full w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur transition"
          title="Saha bildirimleri"
        >
          <AlertTriangle size={15} className={unread > 0 ? "text-amber-300" : ""} />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shadow-md animate-pulse"
              data-testid="saha-issue-bell-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[320px] p-0 rounded-2xl border border-brand-navy/10 shadow-xl overflow-hidden"
        data-testid="saha-issue-popover"
      >
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-700" />
          <div className="text-sm font-semibold text-brand-navy">Saha Bildirimleri</div>
          <div className="ml-auto text-[11px] rounded-full bg-amber-500 text-white px-2 py-0.5">
            {unread} yeni
          </div>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm" data-testid="saha-issue-popover-empty">
            Şimdilik herhangi bir saha bildirimi yok.
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {items.map((it) => (
              <li
                key={it.id}
                className={`px-4 py-3 hover:bg-canvas-cream/60 transition cursor-pointer ${it.unread ? "bg-amber-50/50" : ""}`}
                onClick={async () => {
                  if (it.unread && onAcknowledge) await onAcknowledge(it.id);
                  setOpen(false);
                  onOpenOne && onOpenOne(it.id);
                }}
                data-testid={`saha-issue-item-${it.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${it.unread ? "bg-amber-500" : "bg-slate-300"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-brand-navy truncate">
                      {it.group_name || it.reservation_number}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {it.hotel_name || "—"}
                      {it.agency_name && <> · {it.agency_name}</>}
                    </div>
                    {it.field_issue_note && (
                      <div className="text-[12px] text-amber-800 mt-1 line-clamp-2 italic">
                        "{it.field_issue_note}"
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                      {it.field_issue_reported_by_name && <span>{it.field_issue_reported_by_name}</span>}
                      {it.field_issue_reported_at && <><span>·</span><span className="font-mono">{_fmtStamp(it.field_issue_reported_at)}</span></>}
                      {!it.unread && <span className="ml-auto text-emerald-700 inline-flex items-center gap-0.5"><CheckIcon size={9}/> Görüldü</span>}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

